import { tryParseUrl } from '~/src/common/utils/url';

import { isObject } from '../../utils/guards';
import { pick } from '../../utils/object';

import { Params } from './types';

/**
 * Le loader est importé dynamiquement en fonction de l'environnement détecté
 * pour éviter de charger du code serveur dans le navigateur, et inversement.
 */
const loader =
  typeof window === 'undefined'
    ? () => import('./server/fetch').then(m => m.serverFetch)
    : () => import('./browser/fetch').then(m => m.browserFetch);

/**
 * Cet utilitaire permet de surcharger individuellement les appels réseaux.
 * L'utilisation d'Orval ne nous permet pas de modifier autre chose que les
 * querystring et le body des appels. Cette fonction permet de passer outre
 * cette limitation, et de surcharger directement les params passé à fetch.
 */
const applyOverrides = (input: Params): Params => {
  const pathname = tryParseUrl(input.url)?.pathname;
  // Désactivation du cache browser pour le recompute
  if (pathname === '/api/cart/recompute2') {
    const extraHeaders = { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' };
    return { ...input, headers: { ...input.headers, ...extraHeaders } };
  }
  // Dans les autres cas, on ne fait rien
  return input;
};

/**
 * Wrapper autour de fetch permettant d'inclure les infos de la requête
 * directement au sein de l'erreur, pour un meilleur monitoring dans Sentry
 */
export const fetch = async <T>(input: Params): Promise<T> => {
  const isoFetch = await loader();
  return isoFetch<T>(applyOverrides(input)).catch(err => {
    const req = pick(input, ['method', 'url', 'params', 'data']);
    const extra = isObject(err) && isObject(err.extra) && err.extra;
    throw Object.assign(err, { extra: { req, ...extra } });
  });
};
